import React, { useState, useEffect } from 'react';
import { Cookie, X } from 'lucide-react';
import { Button } from './ui/button';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Hide the consent banner on embed routes so third-party iframes stay clean.
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/embed/')) {
      return;
    }
    // Check if user has already accepted cookies
    const consent = localStorage.getItem('sdc_cookie_consent');
    if (!consent) {
      // Small delay to prevent flash on page load
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptCookies = () => {
    localStorage.setItem('sdc_cookie_consent', 'accepted');
    localStorage.setItem('sdc_cookie_consent_date', new Date().toISOString());
    setShowBanner(false);
    
    // Enable tracking after consent
    if (window.SDC && window.SDC.META_PIXEL_ID && typeof window.fbq === 'function') {
      window.fbq('consent', 'grant');
    }
    
    // Track consent event
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted',
        'ad_storage': 'granted'
      });
    }
  };

  const declineCookies = () => {
    localStorage.setItem('sdc_cookie_consent', 'declined');
    localStorage.setItem('sdc_cookie_consent_date', new Date().toISOString());
    setShowBanner(false);
    
    // Disable tracking
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied'
      });
    }
  };

  if (!showBanner) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-[9998] bg-[#1a1c1b] border-t border-[#3f4816] shadow-2xl"
      role="dialog"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-center sm:text-left">
            <Cookie className="w-6 h-6 text-[#d9fb06] flex-shrink-0 hidden sm:block" />
            <p className="text-sm text-[#888680]">
              We use cookies to improve your experience, analyze site traffic, and serve personalized ads. 
              By clicking "Accept", you consent to our use of cookies. 
              <a href="/privacy-policy" className="text-[#d9fb06] hover:underline ml-1">
                Privacy Policy
              </a>
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <Button
              onClick={declineCookies}
              variant="outline"
              size="sm"
              className="border-[#3f4816] text-[#888680] hover:bg-[#302f2c] hover:text-[#dfddd6]"
              data-testid="cookie-decline-btn"
            >
              Decline
            </Button>
            <Button
              onClick={acceptCookies}
              size="sm"
              className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 font-bold"
              data-testid="cookie-accept-btn"
            >
              Accept All
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
